<script setup lang="ts">
import { computed } from 'vue';
import { I18nKey } from '@/utils/typings';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import { createPermissionsDictionary } from '../utils';
import { PermissionDto } from '@/api/generator';
import FormSelectionDisplay from '@/components/forms/display/FormSelectionDisplay.vue';
import { useTranslation } from 'i18next-vue';
import { SelectItemTypeObject } from '@/components/forms/types';

interface ViewPermissionsProps {
  allPermissions: PermissionDto[];
  grantedPermissions: string[];
}

const props = defineProps<ViewPermissionsProps>();

const { t } = useTranslation();

const groupedPermissions = computed(() =>
  groupPermissionsByRoot(props.grantedPermissions, props.allPermissions),
);

function groupPermissionsByRoot(
  grantedPermissions: string[] | undefined | null,
  allPermissions: PermissionDto[],
) {
  const dictionary = createPermissionsDictionary(allPermissions);

  const groupedPermissionsResult: Record<
    string,
    { group: PermissionDto; children: SelectItemTypeObject<string>[] }
  > = {};

  grantedPermissions?.forEach((grantedPermission) => {
    const targetPermission = dictionary[grantedPermission];
    if (!targetPermission.group) {
      return;
    }

    if (!groupedPermissionsResult[targetPermission.group]) {
      groupedPermissionsResult[targetPermission.group] = {
        group: dictionary[targetPermission.group],
        children: [],
      };
    }
    if (targetPermission.i18n && targetPermission.id) {
      groupedPermissionsResult[targetPermission.group].children.push({
        title: t(targetPermission.i18n as I18nKey),
        value: targetPermission.id,
      });
    }
  });

  return groupedPermissionsResult;
}
</script>

<template>
  <div>
    <FormLabel class="mt-10" :label="$t('Permission_other')"></FormLabel>
    <template v-for="permission in groupedPermissions" :key="`${permission.group.id}`">
      <div class="text-caption mb-1">
        {{ $t(permission.group.i18n as I18nKey) }}
      </div>

      <FormSelectionDisplay :value="permission.children"></FormSelectionDisplay>
    </template>
  </div>
</template>

<style scoped lang="scss">
.card {
  min-height: 100%;
}
</style>
